// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useState } from 'react';

import type { StakeKind } from './types';

/** A recorded boundary stake, as the admin map reads it back. */
export type CourseStake = {
  id: string;
  holeNumber: number;
  stakeKind: StakeKind | string;
  lat: number;
  lon: number;
  accuracyMeters: number;
  headingDegrees: number | null;
  capturedAt: string;
  hasPhoto: boolean;
  reviewStatus: 'pending' | 'accepted' | 'rejected';
};

export type CourseStakesState = {
  stakes: CourseStake[];
  loading: boolean;
  refresh: () => void;
  /** Accepts or rejects a stake. A rejected stake leaves the line but stays on the map. */
  review: (stakeId: string, accepted: boolean) => Promise<boolean>;
};

type Row = Record<string, unknown>;

const num = (row: Row, key: string): number => {
  const value = row[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
};

const mapStake = (row: Row): CourseStake | null => {
  const id = typeof row.id === 'string' ? row.id : null;
  if (!id) return null;
  const heading = row.headingDegrees;
  const status = row.reviewStatus;
  return {
    id,
    holeNumber: num(row, 'holeNumber'),
    stakeKind: typeof row.stakeKind === 'string' ? row.stakeKind : 'out-of-bounds',
    lat: num(row, 'lat'),
    lon: num(row, 'lon'),
    accuracyMeters: num(row, 'accuracyMeters'),
    headingDegrees: typeof heading === 'number' && Number.isFinite(heading) ? heading : null,
    capturedAt: typeof row.capturedAt === 'string' ? row.capturedAt : '',
    hasPhoto: row.hasPhoto === true,
    reviewStatus: status === 'accepted' || status === 'rejected' ? status : 'pending',
  };
};

/** The stakes recorded on a course. Admin only; the endpoint refuses everybody else. */
export function useCourseStakes(
  apiBaseUrl: string,
  token: string | null | undefined,
  courseId: string | null,
): CourseStakesState {
  const [stakes, setStakes] = useState<CourseStake[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    if (!token || !courseId) {
      setStakes([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const load = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}/stakes`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        if (!response.ok) {
          setStakes([]);
          return;
        }
        const payload = (await response.json()) as Row[];
        if (cancelled || !Array.isArray(payload)) return;
        setStakes(payload.map(mapStake).filter((s): s is CourseStake => s !== null));
      } catch {
        if (!cancelled) setStakes([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, courseId, token, reloadKey]);

  const review = useCallback(
    async (stakeId: string, accepted: boolean): Promise<boolean> => {
      if (!token || !courseId) return false;
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}/stakes/${encodeURIComponent(stakeId)}/review`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ accepted }),
          },
        );
        if (!response.ok) return false;
        refresh();
        return true;
      } catch {
        return false;
      }
    },
    [apiBaseUrl, courseId, token, refresh],
  );

  return { stakes, loading, refresh, review };
}
